import React, { useMemo } from 'react';
import { Box, Breadcrumbs, Typography, Container } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Redirect, useHistory, useParams } from 'react-router';
import { Alert, DownloadCSVButton, ProviderLayout, UpliftThemeProvider, useAlert } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';
import { useIsMobile, useL10n, useUpBreakpoint } from '@uplift/hooks';

import { ROUTING_CONFIG } from '@uplift/constants';
import { FinanceMonthTable } from './components/FinanceMonthTable/FinanceMonthTable.component';
import { CloudDownloadOutlined } from '@mui/icons-material';
import { convertFinanceMonthRowsToCsvData, getCurrentPeriod } from './helpers';
import { FinanceMonthPeriodFilter } from './types';
import { PeriodFilter } from './components/PeriodFilter/PeriodFilter.component';
import { SummaryCards } from './components/SummaryCards/SummaryCards.component';
import { useCurrentuser } from '@uplift/contexts';
import { useFinancePaymentDashboardQuery } from '@uplift/queries';

export type FinanceMonthPageParams = {
  date: string;
  filter: FinanceMonthPeriodFilter;
};

const useFinanceMonthStyles = makeStyles()((theme) => ({
  breadcrumbLink: {
    cursor: 'pointer',
  },
  whiteButton: {
    background: theme.palette.common.white,
  }
}));

const FinanceMonthPageContent = () => {
  const l10n = useL10n();
  const alert = useAlert();
  const history = useHistory();
  const isSmUp = useUpBreakpoint('sm');
  const { classes } = useFinanceMonthStyles();
  const isMobile = useIsMobile();
  const me = useCurrentuser();
  const { date, filter } = useParams<FinanceMonthPageParams>();

  const currentDate = React.useMemo(() => CalendarDateService.date(date, 'YYYY-MM'), [ date ]);
  const currentDateStr = React.useMemo(() => currentDate.set('date', 20).utc().startOf('day').format(), [ currentDate ]);
  const title = React.useMemo(() => currentDate.format('MMMM YYYY'), [ currentDate ]);
  const { data: periodsResult, isLoading } = useFinancePaymentDashboardQuery(me.id, currentDateStr);
  const currentPeriod = React.useMemo(() => getCurrentPeriod(periodsResult?.data, filter), [ periodsResult, filter ]);
  const rows = useMemo(() => {
    if (!currentPeriod?.items) {
      return [];
    }

    return currentPeriod.items.sort((a, b) =>
      CalendarDateService.date(a.submittedAt).isSameOrAfter(CalendarDateService.date(b.submittedAt))
        ? -1
        : 1
    );
  }, [ currentPeriod.items ]);

  const { csvData, csvHeaders, csvFileName } = useMemo(() => {
    const csvFileName = l10n.finance.monthly.csvTitle(currentDate);

    return { ...convertFinanceMonthRowsToCsvData(rows), csvFileName };
  }, [ l10n.finance.monthly, currentDate, rows ]);

  const onCsvDownload = () => {
    alert.success(<Box fontWeight={700}>{l10n.finance.csvDownloaded}</Box>);
  };

  const hasWarning = rows.some(a => a.payment === null);

  return (
    <>
      <Box pt={5} mb={2.5} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Breadcrumbs aria-label="breadcrumb">
          <Typography
            className={classes.breadcrumbLink}
            color="textSecondary"
            onClick={() => history.push(ROUTING_CONFIG.finance)}
          >
            {l10n.finance.title}
          </Typography>
          <Typography color="textPrimary" aria-current="page">
            {title}
          </Typography>
        </Breadcrumbs>
        <Box display="flex" gap="1rem">
          {!me.rules?.msoPayout && (
            <PeriodFilter
              periodFilter={filter}
              date={date}
              className={classes.whiteButton}
            />
          )}
          {isSmUp && (
            <DownloadCSVButton
              variant="outlined"
              startIcon={<CloudDownloadOutlined />}
              data={csvData}
              headers={csvHeaders}
              filename={csvFileName}
              size={isMobile ? 'small' : 'medium'}
              className={classes.whiteButton}
              onSuccess={onCsvDownload}
            >
              {l10n.general.download}
            </DownloadCSVButton>
          )}
        </Box>
      </Box>

      {hasWarning && (
        <Box mb={2}>
          <Alert severity="info">{l10n.finance.monthly.calculationWarning}</Alert>
        </Box>
      )}

      {isSmUp && (
        <Box>
          <SummaryCards {...currentPeriod} msoPayout={me.rules?.msoPayout} />
        </Box>
      )}

      {me.rules?.msoPayout && (
        <Box mt={2}>
          <Typography variant="body2" color="textPrimary">
            {l10n.finance.payoutSubtitle}
          </Typography>
        </Box>
      )}

      <Box mt={4} mb={6}>
        <FinanceMonthTable isLoading={isLoading} rows={rows} />
      </Box>
    </>
  );
};

export const FinanceMonthPage: React.FC = () => {
  const { date } = useParams<FinanceMonthPageParams>();

  if (!CalendarDateService.date(date, 'YYYY-MM').isValid()) {
    return <Redirect to={ROUTING_CONFIG.finance} />;
  }

  return (
    <ProviderLayout>
      <UpliftThemeProvider>
        <Container>
          <FinanceMonthPageContent />
        </Container>
      </UpliftThemeProvider>
    </ProviderLayout>
  );
};
